import DateFnsUtils from '@date-io/date-fns';
import { Grid, InputAdornment, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { CalendarIcon, InvertedChevronIcon, SyncIcon } from 'components/Icons';
import ptBR from 'date-fns/locale/pt-BR';
import AssembleiaProduto from 'models/AssembleiaProduto';
import { ListaEventoRequest } from 'models/ListaEventoRequest';
import moment from 'moment';
import React, { useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IReducer } from 'store/Reducer';
import { filtrosInitialState, filtrosReducer } from './reducer';
import useStyle from './style';

interface Props {
  onChangeFilter: (filters: Partial<ListaEventoRequest> | undefined) => void;
  onValidateDate: (hasError: boolean) => void;
}

export default function Filtros({ onChangeFilter, onValidateDate }: Props) {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();
  // const { cooperativas, cooperativasIsLoading } = useSelector((state: IReducer) => state.assembleiasAssessor);
  const {tipos, loadingTipos, tiposProduto, loadingTiposProduto, produtosPorTipo, loadingProdutosPorTipo} = useSelector((state: IReducer) => state.assembleiasAssessor)
  const [{ tipo, tipoProduto, produto, startDate, endDate }, action] = useReducer(filtrosReducer, filtrosInitialState);

  const cooperativas: any[] = [];
  const cooperativasIsLoading = false;

  const maxStartDate = useMemo(() => {
    return new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, 0);
  }, [endDate]);

  const hasDateError = useMemo(() => {
    if (endDate < startDate) return true;
    if (startDate > maxStartDate) return true;
    return false;
  }, [maxStartDate, startDate, endDate]);

  const filters: Partial<ListaEventoRequest> | undefined = useMemo(() => {
    if (hasDateError) return;
    return {
      tipo,
      tipoProduto,
      produto,
      dataInicial: moment(startDate).format('YYYY-MM-DDTHH:mm:ss'),
      dataFinal: moment(endDate).format('YYYY-MM-DDTHH:mm:ss'),
    } as Partial<ListaEventoRequest>;
  }, [tipo, tipoProduto, produto, hasDateError, startDate, endDate]);

  useEffect(() => {
    // if (cooperativas !== null || cooperativasIsLoading) return;
    // dispatch(assessorActionListarCooperativasUsuario(undefined, ErrorHandler));
  }, [dispatch, cooperativas, cooperativasIsLoading]);

  useEffect(() => {
    onChangeFilter(filters);
  }, [filters, onChangeFilter]);

  useEffect(() => {
    onValidateDate(hasDateError);
  }, [hasDateError, onValidateDate]);

  const allProdutosPorTipo = useMemo<AssembleiaProduto[]>(() => {
    const tipo: number = filters?.tipoProduto || -1;
    return produtosPorTipo.find(it => it.idTipoProduto === tipo)?.produtos || [];
  }, [filters, produtosPorTipo])

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={tipos || []}
            getOptionLabel={option => `${option.titulo}`}
            color="primary"
            disabled={loadingTipos}
            onChange={(e, newValue) => action({ type: 'setTipo', value: newValue?.id })}
            noOptionsText={t('TelaInicialAssessor.filtroTipo.noOptionsText')}
            popupIcon={
              loadingTipos ? (
                <SyncIcon className={classes.selectIcon} animated />
              ) : (
                <InvertedChevronIcon className={classes.selectIcon} />
              )
            }
            fullWidth
            renderInput={params => <TextField {...params} label={t('TelaInicialAssessor.filtroTipo.label')} />}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={tiposProduto || []}
            getOptionLabel={option => `${option.titulo}`}
            color="primary"
            disabled={loadingTiposProduto}
            onChange={(e, newValue) => action({ type: 'setTipoProduto', value: newValue?.id })}
            noOptionsText={t('TelaInicialAssessor.filtroTipoProduto.noOptionsText')}
            popupIcon={
              loadingTiposProduto ? (
                <SyncIcon className={classes.selectIcon} animated />
              ) : (
                <InvertedChevronIcon className={classes.selectIcon} />
              )
            }
            fullWidth
            renderInput={params => <TextField {...params} label={t('TelaInicialAssessor.filtroTipoProduto.label')} />}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={allProdutosPorTipo}
            getOptionLabel={option => `${option.codigo} - ${option.titulo}`}
            getOptionSelected={(option: AssembleiaProduto, value: AssembleiaProduto) => option.idProduto === value.idProduto}
            color="primary"
            disabled={loadingProdutosPorTipo || allProdutosPorTipo.length === 0}
            onChange={(e, newValue) => action({ type: 'setProduto', value: newValue?.idProduto })}
            noOptionsText={t('TelaInicialAssessor.filtroProduto.noOptionsText')}
            popupIcon={
              loadingProdutosPorTipo ? (
                <SyncIcon className={classes.selectIcon} animated />
              ) : (
                <InvertedChevronIcon className={classes.selectIcon} />
              )
            }
            fullWidth
            renderInput={params => <TextField {...params} label={t('TelaInicialAssessor.filtroProduto.label')} />}
          />
        </Grid>
        {/* <Grid item xs={12} sm={6}>
          <Autocomplete
            options={cooperativas || []}
            getOptionLabel={option => `${option.codCooperativa} - ${option.nomeCooperativa}`}
            color="primary"
            disabled={cooperativasIsLoading}
            onChange={(e, newValue) => action({ type: 'setCoop', value: newValue?.codCooperativa })}
            noOptionsText={t('TelaInicialAssessor.filtroCoop.noOptionsText')}
            popupIcon={
              cooperativasIsLoading ? (
                <SyncIcon className={classes.selectIcon} animated />
              ) : (
                <InvertedChevronIcon className={classes.selectIcon} />
              )
            }
            fullWidth
            renderInput={params => <TextField {...params} label={t('TelaInicialAssessor.filtroCoop.label')} />}
          />
        </Grid> */}
        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            label={t('TelaInicialAssessor.filtroStartDate.label')}
            value={startDate}
            onChange={newDate =>
              action({
                type: 'setStartDate',
                value: newDate ? new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), 0, 0, 0) : null,
              })
            }
            autoOk
            maxDate={maxStartDate}
            format="'De' dd/MM/yyyy"
            color="primary"
            // cancelLabel={t('TelaInicialAssessor.filtroStartDate.cancelLabel')}
            maxDateMessage={t('TelaInicialAssessor.filtroStartDate.maxDateError')}
            minDateMessage={t('TelaInicialAssessor.filtroStartDate.minDateError')}
            inputProps={{
              className: classes.dateField,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" className={classes.dateFieldAdornment}>
                  <CalendarIcon />
                </InputAdornment>
              ),
            }}
            variant="inline"
            disabled={cooperativasIsLoading}
            disableToolbar
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={3} md={3}>
          <DatePicker
            label={t('TelaInicialAssessor.filtroEndDate.label')}
            value={endDate}
            onChange={newDate =>
              action({
                type: 'setEndDate',
                value: newDate
                  ? new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), 23, 59, 59)
                  : null,
              })
            }
            autoOk
            format="'Até' dd/MM/yyyy"
            color="primary"
            // cancelLabel={t('TelaInicialAssessor.filtroEndDate.cancelLabel')}
            inputProps={{
              className: classes.dateField,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" className={classes.dateFieldAdornment}>
                  <CalendarIcon />
                </InputAdornment>
              ),
            }}
            variant="inline"
            disabled={cooperativasIsLoading}
            disableToolbar
            required
            fullWidth
          />
        </Grid>
      </Grid>
    </MuiPickersUtilsProvider>
  );
}
